import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import colors from "../styles/colors";
import { HiCheckBadge } from "react-icons/hi2";
import { PiHandshake } from "react-icons/pi";
import { FaHeadSideVirus } from "react-icons/fa6";
import { HiOutlineLockOpen } from "react-icons/hi2";
import ContactUsSection from "../components/HomeSectionContainers/ContactUsSectoin";
import { useTranslation } from "react-i18next";
import SEO from "../utils/SEO";
import { SlArrowDown } from "react-icons/sl";

const About: React.FC = () => {
  const { t } = useTranslation();
  const [experience, setExperience] = useState(0);
  const [projects, setProjects] = useState(0);
  const [startCounter, setStartCounter] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
    };
  }, []);

  const scrollDown = () => {
    // Az oldal lefelé görgetése 600px-t
    window.scrollBy({ top: 700, left: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (startCounter) {
      const totalDuration = 2000;
      const intervalTimeExp = totalDuration / 30;
      const intervalTimeProj = totalDuration / 500;

      const expInterval = setInterval(() => {
        setExperience((prev) => (prev < 30 ? prev + 1 : prev));
      }, intervalTimeExp);

      const projInterval = setInterval(() => {
        setProjects((prev) => (prev < 500 ? prev + 1 : prev));
      }, intervalTimeProj);

      return () => {
        clearInterval(expInterval);
        clearInterval(projInterval);
      };
    }
  }, [startCounter]);

  return (
    <div className="w-full font-serif">
      {/* SEO meta tags */}
      <SEO
        title={t("seo_meta.about.metaTitle")}
        description={t("seo_meta.about.metaDescription")}
      />
      {/* Hero Section - Teljes képernyős háttér + cím */}
      <motion.div
        className="h-[80vh] lg:h-screen w-full flex flex-col items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/about/muhely1.webp')",
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Sötétítő réteg  */}
        <div className="h-[80vh] lg:h-screen absolute inset-0 bg-black opacity-50"></div>
        {/* title */}
        <motion.h1
          className="text-4xl sm:text-6xl md:text-8xl mt-24 md:mt-48 z-10"
          style={{ color: colors.lightCream }}
          initial={{ y: 35, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 0.2, duration: 0.6 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {t("aboutPage.title")}
        </motion.h1>
        {/* icon */}
        <motion.div
          className="mt-24 sm:mt-48"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 1.5, duration: 1 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          <SlArrowDown
            className="text-3xl sm:text-4xl text-white animate-bounce hover:text-gray-300 cursor-pointer"
            onClick={scrollDown}
          />
        </motion.div>
      </motion.div>

      {/* Main Content */}
      <div
        className="px-8 py-24 sm:px-20 md:px-40"
        style={{ backgroundColor: colors.pastelCream, color: colors.darkBrown }}
      >
        {/* First Section - Szöveg + Kép */}
        <motion.div
          className="flex flex-col lg:flex-row items-center justify-between gap-16 min-h-[50vh]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { duration: 0.7 } }}
          viewport={{ once: true, amount: 0.5 }}
        >
          <p className="lg:text-2xl text-lg md:text-xl leading-relaxed lg:w-1/2 max-w-xl text-left">
            {t("aboutPage.firstText")}
          </p>
          <img
            src="/assets/images/about/about1.webp"
            alt="Workshop"
            className="w-full lg:w-1/2 rounded-2xl shadow-lg"
          />
        </motion.div>

        {/* Second Section - Szöveg + Másik kép */}
        <motion.div
          className="flex flex-col lg:flex-row-reverse items-center justify-between gap-16 min-h-[50vh] mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { duration: 0.7 } }}
          viewport={{ once: true, amount: 0.5 }}
        >
          <p className="lg:text-2xl text-lg md:text-xl leading-relaxed lg:w-1/2 max-w-xl text-left">
            {t("aboutPage.secondText")}
          </p>
          <img
            src="/assets/images/about/about2.webp"
            alt="Production Process"
            className="w-full lg:w-1/2 rounded-2xl shadow-lg"
          />
        </motion.div>

        {/* Counters */}
        <motion.div
          className="flex flex-col sm:flex-row justify-center items-center gap-24 mt-28"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { duration: 0.5 } }}
          viewport={{ once: true, amount: 0.5 }}
          onViewportEnter={() => setStartCounter(true)}
        >
          <div className="flex flex-col items-center">
            <div
              className="flex items-center justify-center rounded-full w-40 h-40 text-5xl font-bold"
              style={{
                backgroundColor: colors.primaryGreen,
                color: colors.lightCream,
              }}
            >
              +{experience}
            </div>
            <p className="text-2xl mt-4">{t("aboutPage.counter1")}</p>
          </div>
          <div className="flex flex-col items-center">
            <div
              className="flex items-center justify-center rounded-full w-40 h-40 text-5xl font-bold"
              style={{
                backgroundColor: colors.primaryGreen,
                color: colors.lightCream,
              }}
            >
              +{projects}
            </div>
            <p className="text-2xl mt-4">{t("aboutPage.counter2")}</p>
          </div>
        </motion.div>
      </div>

      {/* Horizontal Divider */}
      <div
        className="w-full max-h-[20vh] pt-12 pb-12 flex justify-center"
        style={{ backgroundColor: colors.pastelCream }}
      >
        <hr
          className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100 dark:opacity-80"
          style={{ backgroundColor: colors.darkBrown }}
        />
      </div>

      {/* Contact Section */}
      <ContactUsSection />
    </div>
  );
};

export default About;
